import {
	Alert,
	AlertActionLink,
	Breadcrumb,
	BreadcrumbItem,
	Button,
	ClipboardCopy,
	Content,
	ContentVariants,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	Tab,
	Tabs,
} from "@patternfly/react-core";
import type React from "react";
import { type FC, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { ProductListingTypes } from "../../../constants/product";
import "./ProductDetailHeader.scss";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { useControllerContext } from "@rhc-shared-components/control-wrapper";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty, map } from "lodash";
import { useParams } from "react-router";
import { patchProductListing } from "../../../api/pyxis";
import { ProductValidationSchema, ValidateRequiredFields } from "../../../constants/formValidationSchemas";
import { getProductDetailsRouteList, type ProductRouteParams } from "../../../constants/routerconstants";
import { useBadDataState, useSetBadDataState } from "../../../hooks/useBadDataState";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import { usePublishState } from "../../../hooks/usePublishState";
import {
	invalidateProductQueries,
	useChecklistStatus,
	useCompanyDetails,
	useProductDetail,
} from "../../../queries/productDetails";
import Env from "../../../utils/env";
import { getProductTypeNameWithDistributedBy } from "../../../utils/products";
import ProductScore from "./ProductScore/ProductScore";

export type ProductDetailHeaderProps = {};

interface ValidationErrorState {
	[key: string]: {
		status: boolean;
		nonRequiredFieldCheck?: boolean;
		route: string | null;
		url?: string;
	};
}

const ProductDetailHeader: FC<ProductDetailHeaderProps> = () => {
	const queryClient = useQueryClient();
	const { data: companyDetails } = useCompanyDetails();
	const { isReadOnly } = useControllerContext();
	const history = useHistory();
	const { pathname } = useLocation();
	const [validationErrorState, setValidationErrorState] = useState<ValidationErrorState>({});
	const params = useParams<ProductRouteParams>();
	const badDataState = useBadDataState(params?.productId);
	const setBadDataState = useSetBadDataState(params?.productId);
	const publishState = usePublishState(params?.productId);
	const { isFetching } = useProductDetail(params?.productId);
	const {
		productDetail,
		setProductData,
		componentMetadata: { isValid },
	} = useProductMetadata();

	const { mutateAsync: patchProduct, isPending: isSubmitting } = useMutation({
		mutationFn: patchProductListing,
		onSuccess: async (response) => {
			setProductData(response);
			await invalidateProductQueries(queryClient, params.productId);
		},
	});
	const type = useMemo(() => getProductTypeNameWithDistributedBy(productDetail), [productDetail]);
	const [isPublishable, setIsPublishable] = useState(false); // Product has all the required fields filled
	const { setAlertState } = useAlertContext(); // Alert context
	const routes = useMemo(
		() => getProductDetailsRouteList(params?.productId, productDetail),
		[params?.productId, productDetail],
	);
	const [activeTab, setActiveTab] = useState(pathname?.split("/")?.[4]);
	const location = useLocation();
	const match = useRouteMatch<ProductRouteParams>();
	const { data: checklistStatus, isFetching: isChecklistFetching } = useChecklistStatus(match.params.productId);
	useEffect(() => {
		const newTab = location.pathname.split("/")?.[4];
		if (newTab !== activeTab) {
			setActiveTab(newTab);
		}
	}, [location.pathname, activeTab]);

	const onSelect = (_event: React.MouseEvent<HTMLElement>, eventKey: string | number) => {
		const newTab = eventKey.toString();
		if (newTab !== activeTab) {
			const selectedTab = routes.find((route) => route.id === newTab);
			if (selectedTab) {
				history.push(`${selectedTab.path}`);
				setActiveTab(newTab);
			}
		}
	};
	useEffect(() => {
		if (!isFetching && !isEmpty(productDetail)) {
			const validationObject = publishState ? { ...productDetail, ...publishState } : productDetail;
			let hasBadData;
			ProductValidationSchema.validate(validationObject, { abortEarly: false })
				.then(() => {
					hasBadData = false;
				})
				.catch(() => {
					hasBadData = true;
				})
				.finally(() => {
					hasBadData = checklistStatus?.status === "NOT_COMPLETED";
					if ((hasBadData || !isValid) && productDetail?.published) {
						setBadDataState(true);
					} else {
						setBadDataState(false);
					}
				});
		}
	}, [productDetail, checklistStatus, publishState, isValid, isFetching, setBadDataState]);
	useEffect(() => {
		if (!isFetching || isChecklistFetching) {
			const validationObject = publishState ? { ...productDetail, ...publishState } : productDetail;
			ValidateRequiredFields(validationObject, companyDetails).then((errorState: ValidationErrorState) => {
				errorState["Certification Projects"].status = !isValid;
				let _isPublishable = Object.keys(errorState)
					.map((key) => errorState[key].status)
					.every((value) => !value);
				if (_isPublishable) {
					_isPublishable = checklistStatus?.status === "COMPLETED";
				}
				setIsPublishable(_isPublishable);
				setValidationErrorState(errorState);
			});
		}
		// eslint-disable-next-line
	}, [publishState, productDetail, companyDetails, checklistStatus, isChecklistFetching, isFetching, isValid]);

	const unpublishProductListing = async () => {
		const requestData = {
			_id: productDetail?._id,
			published: false,
		};
		try {
			await patchProduct(requestData);
			SuccessPopup(setAlertState, `Unpublished ${productDetail?.name}`);
		} catch (_e) {
			ErrorPopup(setAlertState, "There's an error in un-publishing the product");
		}
	};

	const catalogProductListing = () => {
		if (productDetail?.type === ProductListingTypes.ContainerStack) {
			window.open(`${Env.catalogUrl}software/container-stacks/detail/${productDetail?._id}`);
		} else if (productDetail?.type === ProductListingTypes.TraditionalApplication) {
			window.open(`${Env.catalogUrl}software/applications/detail/${productDetail?._id}`);
		} else if (productDetail?.type === ProductListingTypes.OpenStackInfrastructure) {
			window.open(`${Env.catalogUrl}software/openstack-infra/detail/${productDetail?._id}`);
		}
	};

	return (
		<div className="product-header-section">
			<Grid>
				<GridItem span={10} offset={1}>
					<Breadcrumb>
						<BreadcrumbItem
							component={(props: React.ComponentProps<typeof Link>) => (
								<Link {...props} to={`${Env.routePrefix}/products`} />
							)}
							to={`${Env.routePrefix}/products`}
						>
							Product listings
						</BreadcrumbItem>
						<BreadcrumbItem isActive={true}>{productDetail?.name}</BreadcrumbItem>
					</Breadcrumb>
				</GridItem>
				<GridItem span={10} offset={1} className="product-header-details">
					<Grid hasGutter={true}>
						<GridItem span={7}>
							<Content>
								<Content className="product-header-details__title" component={ContentVariants.h1}>
									{productDetail?.name}
								</Content>
								<Content className="product-header-details__type" component={ContentVariants.p}>
									{type}
								</Content>
							</Content>
						</GridItem>
						<GridItem span={5}>
							<Grid hasGutter={true}>
								<GridItem span={12}>
									<Flex
										direction={{
											default: "rowReverse",
										}}
									>
										{productDetail?.published ? (
											<>
												<FlexItem>
													<Button
														variant="secondary"
														data-analytics-category="Product Info|Product Status Update"
														isDisabled={isSubmitting || isReadOnly}
														isLoading={isSubmitting}
														onClick={unpublishProductListing}
													>
														Unpublish
													</Button>
												</FlexItem>
												<FlexItem>
													<Button
														variant="secondary"
														data-analytics-category="Product Info|View Product"
														isDisabled={isReadOnly}
														onClick={catalogProductListing}
													>
														View product
													</Button>
												</FlexItem>
											</>
										) : (
											<FlexItem>
												<Button
													variant="primary"
													data-analytics-category="Product Info|Product Status Update"
													isLoading={isSubmitting}
													onClick={async () => {
														if (!isPublishable) {
															return;
														}
														const requestData = {
															_id: productDetail?._id,
															published: true,
															...publishState,
														};
														try {
															const response = await patchProduct(requestData);
															SuccessPopup(setAlertState, `Published ${response?.name}`);
														} catch (e: unknown) {
															ErrorPopup(
																setAlertState,
																"There's an error in publishing this product listing",
																(e as { detail?: string })?.detail,
															);
														}
													}}
													isDisabled={!isPublishable || isReadOnly || isSubmitting}
												>
													Publish
												</Button>
											</FlexItem>
										)}
										<FlexItem alignSelf={{ default: "alignSelfCenter" }}>
											<Flex spaceItems={{ default: "spaceItemsNone" }}>
												<FlexItem>
													<Content>
														<Content component="p">
															<b>Product ID:</b>
														</Content>
													</Content>
												</FlexItem>
												<FlexItem>
													<ClipboardCopy style={{ background: "transparent" }} variant="inline-compact">
														{productDetail?._id}
													</ClipboardCopy>
												</FlexItem>
											</Flex>
										</FlexItem>
										{/*<FlexItem>*/}
										{/*    <Button icon={<ExternalLinkAltIcon />} variant={"secondary"}>*/}
										{/*        Preview*/}
										{/*</FlexItem>*/}
									</Flex>
								</GridItem>
								<GridItem span={12}>
									<ProductScore isPublishable={isPublishable} validationErrorState={validationErrorState} />
								</GridItem>
							</Grid>
						</GridItem>
						{badDataState ? (
							<Alert
								variant="info"
								isInline
								title={"This product listing is published but is missing some required fields"}
								actionLinks={
									<AlertActionLink onClick={unpublishProductListing}>Unpublish product listing</AlertActionLink>
								}
							>
								Product listings now support additional types of content to market your product to Red Hat Customers in
								the Ecosystem Catalog. To save any new changes to this product listing, you will need to unpublish and
								complete all the additional required fields.
							</Alert>
						) : null}
					</Grid>
				</GridItem>
				<GridItem span={10} offset={1}>
					<Tabs activeKey={activeTab} onSelect={onSelect} role="region">
						{map(routes, (nav, index) => {
							return <Tab eventKey={nav.id} key={index} title={nav.text} tabContentId={nav.id} />;
						})}
					</Tabs>
				</GridItem>
			</Grid>
		</div>
	);
};

export default ProductDetailHeader;
